import { all, call, put, select } from 'redux-saga/effects';

import { apiRequest } from '../../api/api.saga-services';
import { apiSelectors } from '../../api/api.selectors';
import * as api from '../../api/request-config/payment.api';
import { paymentMethodSelectors } from './payment-method.selectors';

import * as actions from './payment-method.actions';
import * as types from './payment-method.types';

const normalizePaymentMethod = (paymentMethod: types.IPaymentMethod): types.IPaymentMethod => ({
  ...paymentMethod,
  id: String(paymentMethod.id),
});

export function* storePaymentMethod(paymentMethod: types.IPaymentMethod) {
  yield put(actions.storePaymentMethod(paymentMethod));
  return paymentMethod;
}

export function* clearPaymentMethod(paymentMethodId: types.IPaymentMethod['id']) {
  yield put(actions.clearPaymentMethod(paymentMethodId));
}

export function* clearAllPaymentMethods() {
  const paymentMethodIds: types.IPaymentMethod['id'][] = yield select(paymentMethodSelectors.selectResourceIds);
  yield put(actions.clearPaymentMethod(paymentMethodIds));
}

export function* getPaymentMethods() {
  const paymentMethods = (yield call(apiRequest, api.getPaymentMethodsApi())) as types.IPaymentMethod[] | null;
  if (!paymentMethods) {
    return;
  }

  yield call(clearAllPaymentMethods);
  const normalized = paymentMethods.map(normalizePaymentMethod);
  yield all(normalized.map((paymentMethod) => call(storePaymentMethod, paymentMethod)));
  return normalized;
}

export function* createSetupIntent() {
  return (yield call(apiRequest, api.createSetupIntentApi())) as types.ISetupIntentSession | null;
}

export function* createPaymentMethod(token: string, externalSetupRef: string) {
  const paymentMethod = (yield call(
    apiRequest,
    api.createPaymentMethodApi(token, externalSetupRef),
  )) as types.IPaymentMethod | null;
  if (!paymentMethod) {
    return;
  }

  const normalized = normalizePaymentMethod(paymentMethod);
  yield call(storePaymentMethod, normalized);
  return normalized;
}

export function* setDefaultPaymentMethod(paymentMethodId: types.IPaymentMethod['id']) {
  const paymentMethod = (yield call(
    apiRequest,
    api.setDefaultPaymentMethodApi(paymentMethodId),
  )) as types.IPaymentMethod | null;
  if (!paymentMethod) {
    return;
  }

  const normalized = normalizePaymentMethod(paymentMethod);
  yield call(storePaymentMethod, normalized);
  return normalized;
}

export function* deletePaymentMethod(paymentMethodId: types.IPaymentMethod['id']) {
  // Successful deletes return result: null, which apiRequest also uses on failure.
  // Distinguish via the api error slot for this route key.
  yield call(apiRequest, api.deletePaymentMethodApi(paymentMethodId));
  const error = yield select(apiSelectors.selectApiError, 'deletePaymentMethod');
  return !error;
}
